import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, Send, ShieldAlert, ShieldCheck, ExternalLink } from "lucide-react";

const AdminEmail = () => {
  const [status, setStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const load = () =>
    api.get("/admin/email/status").then((r) => setStatus(r.data)).catch(() => toast.error("Could not load"));

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter your email first"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/email/test", { to: testEmail });
      toast.success(`Sent. Check ${r.data.sent_to} (and the spam folder if it's not in inbox).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const diagnose = async () => {
    setDiagnosing(true);
    setDiag(null);
    try {
      const r = await api.get("/admin/email/diagnose");
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic failed");
    } finally { setDiagnosing(false); }
  };

  if (!status) return <p className="text-cream/75">Loading…</p>;

  const hasKey = !!status.api_key_configured;
  const isSandbox = !!status.using_sandbox_sender;
  const isReady = hasKey && !isSandbox;

  return (
    <div data-testid="admin-email-page">
      <p className="eyebrow text-cream/65 mb-1">Communications</p>
      <h1 className="font-hero text-3xl text-cream flex items-center gap-3 mb-2">
        <Mail size={26} /> Email setup
      </h1>
      <p className="text-cream/65 text-sm mb-8 max-w-2xl">
        Order confirmations, password emails and shipping notifications are delivered via Resend's HTTPS API — no SMTP ports involved, so it works from any host (Hostinger, AWS, etc.).
      </p>

      {/* Status panel */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-3xl mb-6" data-testid="email-status-card">
        <p className="eyebrow mb-4">Current setup</p>

        <div className="space-y-3 text-sm">
          <Row label="Provider" value="Resend (HTTPS API)" />
          <Row
            label="API key"
            value={hasKey
              ? <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={14} /> Configured</span>
              : <span className="text-rose inline-flex items-center gap-1"><ShieldAlert size={14} /> Missing — emails won't send</span>}
          />
          <Row label="From address" value={<code className="font-mono text-charcoal/85">{status.from_email || "(unset)"}</code>} />
          <Row label="Reply-to" value={<code className="font-mono text-charcoal/85">{status.support_email || "(same as From)"}</code>} />
          <Row
            label="Sender domain"
            value={isSandbox
              ? <span className="text-rose inline-flex items-center gap-1"><ShieldAlert size={14} /> Sandbox (poor deliverability)</span>
              : hasKey
              ? <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={14} /> Verified custom domain</span>
              : <span className="text-charcoal/55">—</span>}
          />
        </div>

        <div className={`mt-5 p-4 rounded-xl border text-sm ${isReady ? "bg-emerald-500/10 border-emerald-500/30 text-charcoal" : "bg-rose/10 border-rose/30 text-charcoal"}`}>
          {status.recommendation}
        </div>
      </section>

      {/* Test send */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-3xl mb-6" data-testid="email-test-card">
        <p className="eyebrow mb-1">Send a test email</p>
        <p className="text-xs text-charcoal/60 mb-4">Quick way to confirm delivery before publishing your store. Check both inbox AND spam folder.</p>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="your@email.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            data-testid="email-test-input"
          />
          <button
            onClick={sendTest}
            disabled={sending || !hasKey}
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="email-test-send"
          >
            <Send size={14} /> {sending ? "Sending…" : "Send test"}
          </button>
        </div>
        {!hasKey && (
          <p className="text-xs text-rose mt-2">RESEND_API_KEY is missing — see the setup steps below.</p>
        )}

        {/* Config check (no actual send) */}
        <div className="mt-5 pt-5 border-t border-border">
          <p className="eyebrow mb-1">Config check</p>
          <p className="text-xs text-charcoal/60 mb-3">
            Verifies the env vars look right (key present, sender domain not the sandbox) without sending an email.
          </p>
          <button
            onClick={diagnose}
            disabled={diagnosing}
            className="btn-outline inline-flex items-center gap-2"
            data-testid="email-diagnose"
          >
            {diagnosing ? "Checking…" : "Run config check"}
          </button>

          {diag && (
            <div className="mt-4 space-y-3 text-sm" data-testid="email-diag-result">
              <div className={`p-3 rounded-lg border ${diag.ok ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose/10 border-rose/30"}`}>
                <p className={`text-xs font-semibold uppercase tracking-[0.2em] mb-1 ${diag.ok ? "text-emerald-700" : "text-rose"}`}>
                  {diag.ok ? "Looks good" : `${(diag.issues || []).length} issue${(diag.issues || []).length === 1 ? "" : "s"} found`}
                </p>
                <p className="text-charcoal/80 leading-relaxed">{diag.recommendation}</p>
              </div>
              {(diag.issues || []).length > 0 && (
                <ul className="bg-cream/60 rounded-lg p-3 text-xs space-y-1.5">
                  {diag.issues.map((it, i) => <li key={i} className="text-rose">• {it}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Setup walkthrough */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-3xl" data-testid="email-setup-guide">
        <p className="eyebrow mb-3">Get inbox-grade deliverability (one time, ~10 min)</p>
        <ol className="list-decimal pl-5 space-y-3 text-sm text-charcoal/80">
          <li>
            Make sure <code>RESEND_API_KEY</code> is set in your VPS <code className="bg-cream px-1 py-0.5 rounded text-rose">/opt/rieave/.env</code>. Get one at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-rose hover:underline inline-flex items-center gap-1">resend.com/api-keys <ExternalLink size={12} /></a>.
          </li>
          <li>
            <strong>Verify your domain</strong> at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-rose hover:underline inline-flex items-center gap-1">resend.com/domains <ExternalLink size={12} /></a>. Add the 4 DNS records Resend provides (SPF, DKIM × 2, MX) to your domain registrar (Hostinger, GoDaddy, etc). Wait 5–15 min, then click <strong>Verify</strong> in Resend until everything is green.
          </li>
          <li>
            Update <code>RESEND_FROM_EMAIL</code> in your VPS .env to use the verified domain:
            <pre className="bg-charcoal text-cream/95 rounded-lg p-3 mt-2 overflow-x-auto text-xs"><code>{`RESEND_FROM_EMAIL="Rieave <orders@rieave.com>"
SUPPORT_EMAIL="info@rieave.com"`}</code></pre>
          </li>
          <li>
            Restart the backend container:
            <pre className="bg-charcoal text-cream/95 rounded-lg p-3 mt-2 overflow-x-auto text-xs"><code>{`cd /opt/rieave
docker compose restart backend`}</code></pre>
          </li>
          <li>
            Refresh this page → the status block above should turn green ("Verified custom domain"). Click <strong>Send test</strong> and confirm the email lands in inbox.
          </li>
        </ol>
      </section>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-border/50 last:border-b-0 pb-2">
    <span className="text-charcoal/55 text-xs uppercase tracking-[0.2em]">{label}</span>
    <span>{value}</span>
  </div>
);

export default AdminEmail;
